import { TGetValueFn } from "@components/smart/FieldInfo";
import {
    DocumentEntity,
    EntitySetName,
    IPrPayrollPeriodEntity,
    PrEducationEntity,
    PrPayrollPeriodDocumentEntity,
    PrPayrollPeriodEntity
} from "@odata/GeneratedEntityTypes";
import { CurrencyCode, PrPayrollPeriodStatusCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { FormattingTarget, IFormatOptions, transformToODataString } from "@odata/OData.utils";
import { TFieldDefinition } from "@pages/PageUtils";
import { TableStorageWithPayrollPeriods } from "@pages/payroll/payroll.types";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isDefined } from "@utils/general";
import LocalSettings from "@utils/LocalSettings";
import { logger } from "@utils/log";
import { memoize } from "lodash";

import { BasicInputSizes, FieldType, Sort, ValueType } from "../../enums";
import { TValue } from "../../global.types";
import { isEvalaProduction } from "../../global.utils";
import { Model } from "../../model/Model";
import CurrencyType, { formatCurrencyVariableDecimals } from "../../types/Currency";
import { DATE_MIN, getUtcDayjs } from "../../types/Date";

function getPayrollCurrency(pathToCurrency: string, storage: Model) {
    let currency: CurrencyCode;
    if (pathToCurrency) {
        currency = storage.getValueByPath(pathToCurrency) as CurrencyCode;
    }
    return currency ?? getCompanyCurrency(storage.context);
}

type TSimpleFormatter = ((val: TValue, args: IFormatOptions) => string);

export function getPayrollCurrencyFormatter(pathToCurrency?: string, withUnit = false): TSimpleFormatter {
    return (val, args) => {
        if (!isDefined(val)) {
            return "";
        }
        return formatCurrencyVariableDecimals(val, getPayrollCurrency(pathToCurrency, args.storage), withUnit || args.target === FormattingTarget.Table);
    };
}

export function getPayrollCurrencyTableFormatter(pathToCurrency?: string): TSimpleFormatter {
    return getPayrollCurrencyFormatter(pathToCurrency, true);
}

export function getPayrollCurrencyUnit(pathToCurrency?: string): TGetValueFn<string> {
    return (args) =>
        CurrencyType.getCurrencyUnit(getPayrollCurrency(pathToCurrency, args.storage));
}

export function getPayrollCurrencyFieldDef(pathToCurrency?: string): TFieldDefinition {
    return {
        formatter: getPayrollCurrencyFormatter(pathToCurrency),
        fieldSettings: {
            unit: getPayrollCurrencyUnit(pathToCurrency)
        }
    };
}

interface IFetchPayrollArgs {
    status?: PrPayrollPeriodStatusCode | PrPayrollPeriodStatusCode[];
    limit?: number;
    dir?: Sort;
    month?: number;
    year?: number;
}

export const fetchPayrollPeriods = memoize(async (oData: OData, args?: IFetchPayrollArgs): Promise<IPrPayrollPeriodEntity[]> => {
    // fetch payroll periods
    try {
        let query = oData.getEntitySetWrapper(EntitySetName.PrPayrollPeriods).query()
            .select(PrPayrollPeriodEntity.Id, PrPayrollPeriodEntity.Month, PrPayrollPeriodEntity.Year, PrPayrollPeriodEntity.StatusCode)
            .orderBy(PrPayrollPeriodEntity.Year, args?.dir !== Sort.Desc)
            .orderBy(PrPayrollPeriodEntity.Month, args?.dir !== Sort.Desc);

        const filters: string[] = [];
        if (args?.month) {
            filters.push(`${PrPayrollPeriodEntity.Month} eq ${transformToODataString(args.month, ValueType.Number)}`);
        }
        if (args?.year) {
            filters.push(`${PrPayrollPeriodEntity.Year} eq ${transformToODataString(args.year, ValueType.Number)}`);
        }
        if (args?.status) {
            const joinedValues = transformToODataString(args?.status as string[], ValueType.String);
            query.filter(`${PrPayrollPeriodEntity.StatusCode} in (${joinedValues})`);
        }
        if (filters.length) {
            query = query.filter(filters.join(" and "));
        }

        if (args?.month && args?.year) {
            // if month and year are provided, we expect only one period, so we can use top(1) to optimize query
            query = query.top(1);
        } else if (args?.limit) {
            query = query.top(args.limit);
        }

        if (!isEvalaProduction()) {
            query.expand(PrPayrollPeriodEntity.Documents, (q) =>
                q.select(PrPayrollPeriodDocumentEntity.PeriodDocumentType).expand(PrPayrollPeriodDocumentEntity.Document, (q2) =>
                    q2.select(DocumentEntity.Id, DocumentEntity.NumberOurs, DocumentEntity.DocumentTypeCode)));
        }

        const res = await query.fetchData<IPrPayrollPeriodEntity[]>();
        return res.value;
    } catch (e) {
        logger.error("Error fetching payroll periods", e);
        return [];
    }
    },
    (_oData: OData, args?: IFetchPayrollArgs) => {
        if (!args) return "default";

        // Normalize status so ['A', 'B'] and ['B', 'A'] produce the exact same key
        const normalizedStatus = Array.isArray(args.status)
            ? [...args.status].sort()
            : args.status;

        return JSON.stringify({
            month: args.month ?? null,
            year: args.year ?? null,
            dir: args.dir ?? null,
            limit: args.limit ?? null,
            status: normalizedStatus ?? null,
        });
});

export async function preselectPayrollPeriods(storage: TableStorageWithPayrollPeriods): Promise<void> {
    const payrollPeriods = await fetchPayrollPeriods(storage.oData, { dir: Sort.Asc });

    // todo: should we show periods which are not in the fetched list?
    const now = getUtcDayjs();
    const { Month, Year } = createPeriodFromDate(now.toDate());
    const mandatoryPeriods = [
        // current period
        { Year, Month },
        // previous period
        Month > 1 ? { Month: Month - 1, Year: Year } : { Month: 12, Year: Year - 1 }
    ];

    mandatoryPeriods?.forEach(period => {
        const firstPeriod = payrollPeriods?.[0];
        const lastPeriod = payrollPeriods?.[payrollPeriods.length - 1];
        // if mandatory period is not in the list, add it to the beginning or end according to order
        if (!firstPeriod || firstPeriod.Year > period.Year || (firstPeriod.Year === period.Year && firstPeriod.Month > period.Month)) {
            payrollPeriods.unshift(period);
        }
        if (!lastPeriod || lastPeriod.Year < period.Year || (lastPeriod.Year === period.Year && lastPeriod.Month < period.Month)) {
            payrollPeriods.push(period);
        }
    });

    storage.setCustomData({
        payrollPeriods
    });
}

export function savePeriodId(storage: TableStorageWithPayrollPeriods, periodId: number): void {
    const customData = LocalSettings.get(storage.id)?.customData ?? {};
    LocalSettings.set(storage.id, {
        customData: {
            ...customData,
            selectedPeriodId: periodId
        }
    });
}

export function getSavedPeriodId(storage: TableStorageWithPayrollPeriods): number {
    const { selectedPeriodId } = LocalSettings.get(storage.id)?.customData ?? {};
    return parseInt(selectedPeriodId);
}

export async function preselectPayrollPeriodsAndSelectFirstToProcess(storage: TableStorageWithPayrollPeriods): Promise<IPrPayrollPeriodEntity> {
    await preselectPayrollPeriods(storage);

    // find first period with "toProcess" status
    const { payrollPeriods } = storage.getCustomData();
    let { selectedPeriod } = storage.getCustomData();
    if (!selectedPeriod) {
        if (payrollPeriods?.length) {
            const selectedPeriodId = getSavedPeriodId(storage);
            if (selectedPeriodId) {
                selectedPeriod = payrollPeriods.find(period => period.Id === selectedPeriodId);
            }
            if (!selectedPeriod) {
                selectedPeriod = payrollPeriods
                    .find(period => period.StatusCode === PrPayrollPeriodStatusCode.ToProcess);
            }
            // if there is no "toProcess" period, select the last one
            if (!selectedPeriod) {
                selectedPeriod = payrollPeriods[payrollPeriods.length - 1];
            }
        }

        storage.setCustomData({ selectedPeriod });
    }

    return selectedPeriod;
}

export type TPayrollPeriod = Pick<IPrPayrollPeriodEntity, "Year" | "Month" | "Id">;

export function createDateFromPeriod(period: TPayrollPeriod, date?: number, endOfMonth?: boolean): Date {
    // setting date must be done after setting month and year, otherwise it may overflow to the next month if the date is greater than the number of days in the current month
    let d = getUtcDayjs().set("year", period.Year).set("month", period.Month - 1).set("date", date ?? 1);
    if (endOfMonth) {
        d = d.endOf("month");
    }
    return d.toDate();
}

export function createPeriodFromDate(date: Date): IPrPayrollPeriodEntity {
    const d = getUtcDayjs(date);
    const Month = d.get("month") + 1;
    const Year = d.get("year");
    return { Month, Year, StatusCode: null };
}

export function getPeriodFromDate(date: Date, storage: TableStorageWithPayrollPeriods): IPrPayrollPeriodEntity {
    /* fallback to fake period - periods may be missing in min-max interval, but user can scroll through it using selector */
    const fallbackPeriod: IPrPayrollPeriodEntity = createPeriodFromDate(date);
    const { Month, Year } = fallbackPeriod;
    const existingPeriod = storage.getCustomData().payrollPeriods
        .find(p => p.Month === Month && p.Year === Year);

    return existingPeriod ?? fallbackPeriod;
}

export async function getEndOfLastProcessedPeriod(storage: Model): Promise<Date> {
    const periods = await fetchPayrollPeriods(storage.oData, {
        status: [PrPayrollPeriodStatusCode.ProcessedAndClosed, PrPayrollPeriodStatusCode.Processed],
        limit: 1,
        dir: Sort.Desc
    });
    const last = periods?.[0];
    if (last) {
        return getUtcDayjs().set("year", last.Year).set("month", last.Month - 1).endOf("month").endOf("day").toDate();
    }
    // if there is no last wage date, returns a minimum date, so the current / default rows are editable
    // without planning changes
    return DATE_MIN;
}

export async function getOldestUnprocessedPeriodDate(storage: Model): Promise<Date> {
    const periods = await fetchPayrollPeriods(storage.oData, {
        status: PrPayrollPeriodStatusCode.ToProcess,
        limit: 1,
        dir: Sort.Asc
    });
    const first = periods?.[0];
    if (first) {
        return createDateFromPeriod(first);
    }
    return null;
}

export const educationDef: TFieldDefinition = {
    type: FieldType.ComboBox,
    width: BasicInputSizes.XL,
    fieldSettings: {
        displayName: PrEducationEntity.Code,
        shouldDisplayAdditionalColumns: true,
    },
    additionalProperties: [{ id: PrEducationEntity.Name }],
    columns: [
        { id: PrEducationEntity.Code },
        { id: PrEducationEntity.Name }
    ]
};